/**
 * TendersTab Component
 * Handles payment entry, variance calculation, and submission
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { 
  TrendingUp, 
  TrendingDown, 
  CreditCard, 
  CheckCircle, 
  AlertTriangle,
  Save,
  Loader2,
  Lock
} from 'lucide-react';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value || 0);
};

export const TendersTab = ({
  // Store details
  storeDetails,
  // Sales summary data
  enteredTotalSales,
  totalServiceSales,
  fuelSalesZReading,
  totalSales,
  // Deductions data
  lotteryPrizePaid,
  scratchPrizePaid,
  totalPaidOut,
  incidentDeductions,
  incidentRecoveries,
  // Payment state
  paymentAmounts,
  setPaymentAmounts,
  handleAmountChange,
  handleAmountBlur,
  handleAmountFocus,
  // Fuel cards
  filteredActiveFuelCards,
  fuelCardTransactions,
  setFuelCardTransactions,
  fuelCardTotals,
  // Variance
  totalPayments,
  expectedPayments,
  variance,
  // Submit handling
  existingEntry,
  isEditing,
  isViewMode,
  saving,
  submitDisabled = false,
  tillLock = false,
  tillCash,
  tillCashDiscrepancy,
  tillOtherPayments = {},
  handleSubmit,
  cancelEditing
}) => {
  
  const totalDeductions = (parseFloat(lotteryPrizePaid) || 0) + 
    (parseFloat(scratchPrizePaid) || 0) + 
    totalPaidOut + 
    (storeDetails?.store_type === 'fuel_station' ? incidentDeductions - incidentRecoveries : 0);

  return (
    <div className="space-y-4">
      {/* Sales Summary Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Sales Summary - Left Side */}
        <Card className="bg-blue-500/5 border-blue-500/20">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Barlow Condensed' }}>
              <TrendingUp className="h-4 w-4 text-blue-500" />
              Sales Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <div className="flex justify-between items-center py-1.5 border-b border-border/50">
              <span className="text-muted-foreground">Shop Sales (Z-Reading)</span>
              <span className="font-mono font-medium">{formatCurrency(enteredTotalSales)}</span>
            </div>
            <div className="flex justify-between items-center py-1.5 border-b border-border/50">
              <span className="text-muted-foreground">Total Services</span>
              <span className="font-mono font-medium">{formatCurrency(totalServiceSales)}</span>
            </div>
            {storeDetails?.store_type === 'fuel_station' && (
              <div className="flex justify-between items-center py-1.5 border-b border-border/50">
                <span className="text-muted-foreground">Fuel Sales (Z-Reading)</span>
                <span className="font-mono font-medium">{formatCurrency(fuelSalesZReading)}</span>
              </div>
            )}
            <div className="flex justify-between items-center pt-2 mt-1 border-t-2 border-blue-500/30">
              <span className="font-medium text-blue-600 dark:text-blue-400">TOTAL SALES</span>
              <span className="font-mono font-bold text-blue-600 dark:text-blue-400">
                {formatCurrency(totalSales + (storeDetails?.store_type === 'fuel_station' ? fuelSalesZReading : 0))}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Deductions Summary - Right Side */}
        <Card className="bg-red-500/5 border-red-500/20">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Barlow Condensed' }}>
              <TrendingDown className="h-4 w-4 text-red-500" />
              Deductions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <div className="flex justify-between items-center py-1.5 border-b border-border/50">
              <span className="text-muted-foreground">Lottery Prizes</span>
              <span className="font-mono font-medium text-red-500">-{formatCurrency(parseFloat(lotteryPrizePaid) || 0)}</span>
            </div>
            <div className="flex justify-between items-center py-1.5 border-b border-border/50">
              <span className="text-muted-foreground">Scratch Card Prizes</span>
              <span className="font-mono font-medium text-red-500">-{formatCurrency(parseFloat(scratchPrizePaid) || 0)}</span>
            </div>
            <div className="flex justify-between items-center py-1.5 border-b border-border/50">
              <span className="text-muted-foreground">Paid Outs</span>
              <span className="font-mono font-medium text-red-500">-{formatCurrency(totalPaidOut)}</span>
            </div>
            {storeDetails?.store_type === 'fuel_station' && (
              <div className="flex justify-between items-center py-1.5 border-b border-border/50">
                <span className="text-muted-foreground">Drive Off / Debt</span>
                <span className="font-mono font-medium text-red-500">-{formatCurrency(incidentDeductions)}</span>
              </div>
            )}
            {incidentRecoveries > 0 && (
              <div className="flex justify-between items-center py-1.5 border-b border-border/50">
                <span className="text-muted-foreground">Recovered</span>
                <span className="font-mono font-medium text-green-500">+{formatCurrency(incidentRecoveries)}</span>
              </div>
            )}
            <div className="flex justify-between items-center pt-2 mt-1 border-t-2 border-red-500/30">
              <span className="font-medium text-red-600 dark:text-red-400">TOTAL DEDUCTIONS</span>
              <span className="font-mono font-bold text-red-600 dark:text-red-400">
                -{formatCurrency(totalDeductions)}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payments and Variance Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Payments Section */}
        <Card>
          <CardHeader className="bg-green-500/5 border-b border-green-500/20 pb-3">
            <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Barlow Condensed' }}>
              <CreditCard className="h-4 w-4 text-green-500" />
              Payments Received
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-3 space-y-2">
            {tillLock && (
              <div className="flex items-center gap-2 p-2 rounded-lg border border-blue-500/30 bg-blue-500/10 text-xs text-blue-600 dark:text-blue-400" data-testid="till-lock-banner-tenders">
                <Lock className="h-4 w-4 shrink-0" />
                Voucher, Card Payments, AMEX and fuel cards were filled from the Day End report and are read-only. Cash can be adjusted (needs admin approval).
              </div>
            )}
            {/* Cash - Always first */}
            <div className="flex items-center gap-3">
              <Label className="flex-1 text-sm font-medium">Cash</Label>
              <div className="relative w-28">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
                <Input
                  type="text"
                  value={paymentAmounts['cash'] || paymentAmounts['Cash'] || ''}
                  onChange={(e) => handleAmountChange('cash', e.target.value, setPaymentAmounts, paymentAmounts)}
                  onBlur={() => handleAmountBlur('cash', setPaymentAmounts, paymentAmounts)}
                  onFocus={handleAmountFocus}
                  className="pl-6 h-8 text-sm font-mono text-right"
                  placeholder="0.00"
                  data-testid="payment-cash"
                />
              </div>
            </div>
            {tillCashDiscrepancy !== undefined && tillCashDiscrepancy !== null && (
              <p className={`text-xs text-right font-medium ${tillCashDiscrepancy > 0 ? 'text-green-600' : tillCashDiscrepancy < 0 ? 'text-red-500' : 'text-muted-foreground'}`} data-testid="till-cash-discrepancy">
                Till report cash {tillCashDiscrepancy > 0 ? 'gain (over)' : tillCashDiscrepancy < 0 ? 'loss (short)' : 'difference'}: {formatCurrency(Math.abs(tillCashDiscrepancy))}
              </p>
            )}
            {tillCash !== undefined && Math.abs((parseFloat(paymentAmounts['cash'] || paymentAmounts['Cash'] || 0) || 0) - tillCash) >= 0.005 && (
              <p className="text-xs text-amber-600 text-right" data-testid="cash-adjusted-hint">
                Day End report Cash: {formatCurrency(tillCash)} - changing it needs admin approval
              </p>
            )}

            {/* Voucher - Second (global for all stores) */}
            <div className="flex items-center gap-3">
              <Label className="flex-1 text-sm font-medium">Voucher</Label>
              <div className="relative w-28">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
                <Input
                  type="text"
                  value={paymentAmounts['vouchers'] || paymentAmounts['Voucher'] || paymentAmounts['Vouchers'] || ''}
                  onChange={(e) => handleAmountChange('vouchers', e.target.value, setPaymentAmounts, paymentAmounts)}
                  onBlur={() => handleAmountBlur('vouchers', setPaymentAmounts, paymentAmounts)}
                  onFocus={handleAmountFocus}
                  readOnly={tillLock}
                  className="pl-6 h-8 text-sm font-mono text-right read-only:bg-muted read-only:cursor-not-allowed"
                  placeholder="0.00"
                  data-testid="payment-voucher"
                />
              </div>
            </div>

            {/* Card Payments (BMS) - Third */}
            <div className="flex items-center gap-3">
              <Label className="flex-1 text-sm font-medium">Card Payments (BMS)</Label>
              <div className="relative w-28">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
                <Input
                  type="text"
                  value={paymentAmounts['card'] || paymentAmounts['Card Payments'] || ''}
                  onChange={(e) => handleAmountChange('card', e.target.value, setPaymentAmounts, paymentAmounts)}
                  onBlur={() => handleAmountBlur('card', setPaymentAmounts, paymentAmounts)}
                  onFocus={handleAmountFocus}
                  readOnly={tillLock}
                  className="pl-6 h-8 text-sm font-mono text-right read-only:bg-muted read-only:cursor-not-allowed"
                  placeholder="0.00"
                  data-testid="payment-card-bms"
                />
              </div>
            </div>

            {/* AMEX - Show when amex_tracking_enabled is true */}
            {storeDetails?.amex_tracking_enabled && (
              <div className="flex items-center gap-3">
                <Label className="flex-1 text-sm font-medium text-purple-600">AMEX</Label>
                <div className="relative w-28">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
                  <Input
                    type="text"
                    value={paymentAmounts['amex'] || paymentAmounts['AMEX'] || ''}
                    onChange={(e) => handleAmountChange('amex', e.target.value, setPaymentAmounts, paymentAmounts)}
                    onBlur={() => handleAmountBlur('amex', setPaymentAmounts, paymentAmounts)}
                    onFocus={handleAmountFocus}
                    readOnly={tillLock}
                    className="pl-6 h-8 text-sm font-mono text-right border-purple-500/30 read-only:bg-muted read-only:cursor-not-allowed"
                    placeholder="0.00"
                    data-testid="payment-amex"
                  />
                </div>
              </div>
            )}

            {/* Other Card Payment Methods - from store payment_types */}
            {storeDetails?.payment_types?.filter(pt => {
              const name = (pt.name || '').toLowerCase();
              return !['cash', 'card payments', 'card', 'voucher', 'vouchers'].includes(name);
            }).map(pt => (
              <div key={pt.id || pt.name} className="flex items-center gap-3">
                <Label className="flex-1 text-sm">{pt.name}</Label>
                <div className="relative w-28">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
                  <Input
                    type="text"
                    value={paymentAmounts[pt.id || pt.name] || ''}
                    onChange={(e) => handleAmountChange(pt.id || pt.name, e.target.value, setPaymentAmounts, paymentAmounts)}
                    onBlur={() => handleAmountBlur(pt.id || pt.name, setPaymentAmounts, paymentAmounts)}
                    readOnly={tillLock && (pt.id || pt.name) in tillOtherPayments}
                    onFocus={handleAmountFocus}
                    className="pl-6 h-8 text-sm font-mono text-right read-only:bg-muted read-only:cursor-not-allowed"
                    placeholder="0.00"
                    data-testid={`payment-${pt.name?.toLowerCase().replace(/\s+/g, '-')}`}
                  />
                </div>
              </div>
            ))}

            {/* Fuel Cards Section - For Fuel Stations */}
            {storeDetails?.store_type === 'fuel_station' && storeDetails?.fuel_cards_enabled && (
              <div className="border-t border-border pt-3 mt-3 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-indigo-600 mb-2">
                  <CreditCard className="h-4 w-4" />
                  Fuel Cards
                </div>
                
                {/* Non-Bunkering Cards (Allstar, UK Fuel, DKV, BP) - Input fields */}
                {filteredActiveFuelCards?.some(c => c.category === 'non_bunkering') && (
                  <div className="space-y-2">
                    {filteredActiveFuelCards
                      .filter(c => c.category === 'non_bunkering')
                      .map((card) => (
                        <div key={card.card_type_id} className="flex items-center gap-3">
                          <Label className="flex-1 text-sm">{card.card_name}</Label>
                          <div className="relative w-28">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={fuelCardTransactions.non_bunkering[card.card_name] || ''}
                              onChange={(e) => setFuelCardTransactions({
                                ...fuelCardTransactions,
                                non_bunkering: {
                                  ...fuelCardTransactions.non_bunkering,
                                  [card.card_name]: e.target.value
                                }
                              })}
                              readOnly={tillLock}
                              className="pl-6 h-8 text-sm font-mono text-right read-only:bg-muted read-only:cursor-not-allowed"
                              data-testid={`fc-nonbunk-${card.card_name.toLowerCase().replace(/\s+/g, '-')}`}
                              autoComplete="off"
                            />
                          </div>
                        </div>
                      ))}
                  </div>
                )}
                
                {/* Bunkering Total (Read-only display) */}
                <div className="flex items-center justify-between bg-orange-500/10 rounded-lg px-3 py-2">
                  <span className="text-sm font-medium text-orange-600">Bunkering Total</span>
                  <span className="font-mono font-bold text-orange-600">{formatCurrency(fuelCardTotals?.bunkering_total || 0)}</span>
                </div>
              </div>
            )}

            {/* Variance Display */}
            <div className="border-t border-border pt-3 mt-3 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Total Payments</span>
                <span className="font-bold font-mono">{formatCurrency(totalPayments)}</span>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Expected ({storeDetails?.store_type === 'fuel_station' 
                  ? 'Sales + Fuel - Deductions' 
                  : 'Sales - Deductions'})</span>
                <span className="font-mono">{formatCurrency(expectedPayments)}</span>
              </div>
              <div className={`flex items-center justify-between p-3 rounded-lg ${
                Math.abs(variance) < 0.01 ? 'bg-green-500/10' : 
                Math.abs(variance) <= 5 ? 'bg-yellow-500/10' : 'bg-red-500/10'
              }`}>
                <div className="flex items-center gap-2">
                  {Math.abs(variance) < 0.01 ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <AlertTriangle className={`h-5 w-5 ${Math.abs(variance) <= 5 ? 'text-yellow-500' : 'text-red-500'}`} />
                  )}
                  <div>
                    <span className="font-medium">Variance</span>
                    {Math.abs(variance) >= 0.01 && (
                      <span className={`ml-2 text-sm ${variance > 0 ? 'text-green-500' : 'text-red-500'}`}>
                        ({variance > 0 ? 'Over' : 'Short'})
                      </span>
                    )}
                  </div>
                </div>
                <span className={`text-xl font-bold font-mono ${
                  Math.abs(variance) < 0.01 ? 'text-green-500' : 
                  variance > 0 ? 'text-green-500' : 'text-red-500'
                }`}>
                  {variance >= 0 ? '+' : ''}{formatCurrency(variance)}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Positive (+) = Over (collected more) | Negative (-) = Short (collected less)
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Submit Button - Only on Tenders Tab, hidden in View Mode */}
      {(!existingEntry || isEditing) && !isViewMode && (
        <Card className="bg-green-500/5 border-green-500/20">
          <CardContent className="p-4">
            <div className="flex justify-end gap-3">
              {isEditing && (
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={cancelEditing}
                >
                  Cancel
                </Button>
              )}
              <Button 
                size="lg" 
                onClick={handleSubmit} 
                disabled={saving || submitDisabled}
                className="min-w-[200px]"
                data-testid="submit-sales-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {isEditing ? 'Updating...' : 'Submitting...'}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {isEditing ? 'Update Sales Entry' : 'Submit Sales Entry'}
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TendersTab;
